import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  IconButton,
  Card,
  CardContent,
  Divider,
  Alert
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { createProperty, uploadAgencyAgreement } from '../services/propertyService';
import type { Property } from '../types/property';
import { useAuth } from '../hooks/useAuth';
import { getPropertyViewMode, type PropertyViewMode } from '../utils/roleUtils';
import api from '../services/api';
import { AustralianAddressAutocomplete } from '../components/AustralianAddressAutocomplete';
import { getOperationErrorMessage } from '../utils/errorMessages';

const NewProperty = () => {
  const navigate = useNavigate();
  const { agencySlug } = useParams<{ agencySlug: string }>();
  const { user, agency } = useAuth();
  // Use subscription object if available, fallback to legacy field
  const currentStatus = agency?.subscription?.status || agency?.subscriptionStatus;
  const isSubscriptionActive = currentStatus === 'active' || currentStatus === 'trial';
  const isAdmin = user?.primaryRole === 'admin';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInactiveSubscriptionNotice, setShowInactiveSubscriptionNotice] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [addressInput, setAddressInput] = useState('');
  const [formData, setFormData] = useState<Partial<Property> & { skipConsultation?: boolean }>({
    status: 'consultation',
    currency: 'AUD',
    propertyType: 'House',
    isNewConstruction: false, // Default to "established" (not new construction)
    skipConsultation: false,
    owner: [{
      name: '',
      email: '',
      phone: '',
      preferredContactMethod: 'email'
    }] as Array<{ name: string; email: string; phone: string; preferredContactMethod: 'email' | 'phone' }>,
    address: {
      street: '',
      city: '',
      state: '',
      country: 'Australia',
      postalCode: ''
    },
    features: {
      bedrooms: 0,
      bathrooms: 0,
      parking: 0,
      area: 0,
      totalFloorArea: undefined as number | undefined, // Optional field
      yearBuilt: undefined as number | undefined // Optional field
    } as any,
    primaryAssignee: '',
    secondaryAssignee: '',
    tertiaryAssignee: ''
  });

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const campaignParam = searchParams.get('campaign');
  const propertyViewMode: PropertyViewMode = getPropertyViewMode(user);
  const inferredDefault: 'sale' | 'rental' =
    campaignParam === 'rental'
      ? 'rental'
      : campaignParam === 'sale'
      ? 'sale'
      : propertyViewMode === 'rental'
      ? 'rental'
      : 'sale';
  const [onboardingType, setOnboardingType] = useState<'sale' | 'rental'>(inferredDefault);

  // Fetch agency default state and users
  useEffect(() => {
    // Fetch agency info to get default state and users
    if (agencySlug) {
      fetchAgencyInfo();
      fetchUsers();
    }
  }, [agencySlug]);

  // Set current user as primary assignee by default when users are loaded
  useEffect(() => {
    if (users.length > 0 && user && !formData.primaryAssignee) {
      const currentUser = users.find(u => u._id === user.id);
      if (currentUser) {
        setFormData(prev => ({
          ...prev,
          primaryAssignee: currentUser._id
        }));
      }
    }
  }, [users, user, formData.primaryAssignee]);

  const fetchAgencyInfo = async () => {
    try {
      const response = await api.get(`/agencies/${agencySlug}`);
      const agency = response.data;
      
      // Pre-populate state if agency has a default state and form state is empty
      if (agency.state && !formData.address?.state) {
        setFormData(prev => ({
          ...prev,
          address: {
            ...prev.address!,
            state: agency.state
          }
        }));
      }
    } catch (error) {
      console.error('Failed to fetch agency info:', error);
      // Don't show error to user as this is just for pre-populating state
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get(`/agencies/${agencySlug}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      // Don't show error to user as this is just for team assignment dropdowns
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agencySlug) return;

    setLoading(true);
    setError(null);

    try {
      // 1. Create the property (and optionally skip consultation workflow)
      const createdProperty = await createProperty(agencySlug, {
        ...formData,
        onboardingType,
      } as any);
      
      // 2. If agency agreement file is provided, upload it
      if ((formData as any).agencyAgreementFile) {
         const file = (formData as any).agencyAgreementFile;
         await uploadAgencyAgreement(agencySlug, createdProperty._id, file);
      }

      // Navigate directly to the newly created property details page
      // The property will be fetched fresh on the details page, so it will include the agency agreement
      navigate(`/agencies/${agencySlug}/properties/${createdProperty._id}`);
    } catch (err: any) {
      setError(getOperationErrorMessage(err, 'createProperty'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof Property] as Record<string, any>),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Helper to normalize owners (handle both single owner legacy and array)
  const getOwners = (): Array<{ name: string; email: string; phone: string; preferredContactMethod: 'email' | 'phone' }> => {
    if (!formData.owner) return [{ name: '', email: '', phone: '', preferredContactMethod: 'email' }];
    if (Array.isArray(formData.owner)) {
      return formData.owner.length > 0 ? formData.owner : [{ name: '', email: '', phone: '', preferredContactMethod: 'email' }];
    }
    // Legacy single owner format
    return [formData.owner];
  };

  const owners = getOwners();

  const handleOwnerChange = (index: number, field: string, value: any) => {
    const updatedOwners = [...owners];
    updatedOwners[index] = { ...updatedOwners[index], [field]: value };
    setFormData(prev => ({ ...prev, owner: updatedOwners }));
  };

  const handleAddOwner = () => {
    const updatedOwners = [...owners, { name: '', email: '', phone: '', preferredContactMethod: 'email' }];
    setFormData(prev => ({ ...prev, owner: updatedOwners }));
  };

  const handleRemoveOwner = (index: number) => {
    if (owners.length > 1) {
      const updatedOwners = owners.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, owner: updatedOwners }));
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {!isSubscriptionActive && showInactiveSubscriptionNotice && (
        <Box sx={{ mb: 3 }}>
          <Alert
            severity="warning"
            onClose={() => setShowInactiveSubscriptionNotice(false)}
            sx={{ fontWeight: 600 }}
          >
            Your paid subscription isn’t active yet. Property tools remain available, but Free plan limits apply until payment succeeds.
          </Alert>
        </Box>
      )}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Add New Property
        </Typography>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Vendor Information
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddOwner}
                  variant="outlined"
                  size="small"
                >
                  Add Vendor
                </Button>
              </Box>
            </Grid>

            {owners.map((owner, index) => (
              <React.Fragment key={index}>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1">
                          Vendor {index + 1}
                        </Typography>
                        {owners.length > 1 && (
                          <IconButton
                            onClick={() => handleRemoveOwner(index)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Owner Name"
                            value={owner.name}
                            onChange={(e) => handleOwnerChange(index, 'name', e.target.value)}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Owner Email"
                            type="email"
                            value={owner.email}
                            onChange={(e) => handleOwnerChange(index, 'email', e.target.value)}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Owner Phone"
                            value={owner.phone}
                            onChange={(e) => handleOwnerChange(index, 'phone', e.target.value)}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth required>
                            <InputLabel>Preferred Contact Method</InputLabel>
                            <Select
                              value={owner.preferredContactMethod}
                              label="Preferred Contact Method"
                              onChange={(e) => handleOwnerChange(index, 'preferredContactMethod', e.target.value)}
                            >
                              <MenuItem value="email">Email</MenuItem>
                              <MenuItem value="phone">Phone</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </React.Fragment>
            ))}

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Address
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <AustralianAddressAutocomplete
                value={addressInput}
                onInputChange={setAddressInput}
                onChange={(addressComponents) => {
                  setFormData(prev => ({
                    ...prev,
                    address: {
                      ...prev.address!,
                      ...addressComponents,
                    }
                  }));
                }}
                label="Street Address"
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City/Suburb"
                value={formData.address?.city}
                onChange={(e) => handleChange('address.city', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>State</InputLabel>
                <Select
                  value={formData.address?.state}
                  label="State"
                  onChange={(e) => handleChange('address.state', e.target.value)}
                >
                  <MenuItem value="ACT">Australian Capital Territory</MenuItem>
                  <MenuItem value="NSW">New South Wales</MenuItem>
                  <MenuItem value="NT">Northern Territory</MenuItem>
                  <MenuItem value="QLD">Queensland</MenuItem>
                  <MenuItem value="SA">South Australia</MenuItem>
                  <MenuItem value="TAS">Tasmania</MenuItem>
                  <MenuItem value="VIC">Victoria</MenuItem>
                  <MenuItem value="WA">Western Australia</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.address?.postalCode}
                onChange={(e) => handleChange('address.postalCode', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Campaign Type
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              {propertyViewMode === 'rental' ? (
                <Typography variant="body2" color="text.secondary">
                  This property will use the <strong>Rental Appraisal</strong> workflow (rental campaign), based on your role.
                </Typography>
              ) : propertyViewMode === 'sales' ? (
                <Typography variant="body2" color="text.secondary">
                  This property will use the <strong>Sales Initial Consultation</strong> workflow (sales campaign), based on your role.
                </Typography>
              ) : (
                <FormControl fullWidth required>
                  <InputLabel>Campaign Type</InputLabel>
                  <Select
                    value={onboardingType}
                    label="Campaign Type"
                    onChange={(e) => setOnboardingType(e.target.value as 'sale' | 'rental')}
                  >
                    <MenuItem value="sale">Sales (Sales Initial Consultation)</MenuItem>
                    <MenuItem value="rental">Rental (Rental Appraisal)</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                Property Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Property Type</InputLabel>
                <Select
                  value={formData.propertyType || 'House'}
                  label="Property Type"
                  onChange={(e) => handleChange('propertyType', e.target.value)}
                >
                  <MenuItem value="House">House</MenuItem>
                  <MenuItem value="Duplex">Duplex</MenuItem>
                  <MenuItem value="Townhouse">Townhouse</MenuItem>
                  <MenuItem value="Villa">Villa</MenuItem>
                  <MenuItem value="Unit">Unit</MenuItem>
                  <MenuItem value="Apartment">Apartment</MenuItem>
                  <MenuItem value="Land">Land</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>New or Established*</InputLabel>
                <Select
                  value={formData.isNewConstruction === true ? 'new' : 'established'}
                  label="New or Established*"
                  onChange={(e) => {
                    const isNew = e.target.value === 'new';
                    handleChange('isNewConstruction', isNew);
                  }}
                >
                  <MenuItem value="established">Established property</MenuItem>
                  <MenuItem value="new">New construction</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Features
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Bedrooms"
                value={formData.features?.bedrooms || ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                  handleChange('features.bedrooms', value);
                }}
                required
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Bathrooms"
                value={formData.features?.bathrooms || ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                  handleChange('features.bathrooms', value);
                }}
                required
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Parking Spaces (optional)"
                value={formData.features?.parking ?? ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                  handleChange('features.parking', value);
                }}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Area (m²)"
                value={formData.features?.area || ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                  handleChange('features.area', value);
                }}
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Total Floor Area (m²)"
                value={formData.features?.totalFloorArea || ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : parseFloat(e.target.value);
                  handleChange('features.totalFloorArea', value);
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Year Built (Optional)"
                value={formData.features?.yearBuilt || ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                  handleChange('features.yearBuilt', value);
                }}
                inputProps={{ min: 1800, max: new Date().getFullYear() }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Team Assignment
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Primary Assignee</InputLabel>
                <Select
                  value={formData.primaryAssignee}
                  label="Primary Assignee"
                  onChange={(e) => handleChange('primaryAssignee', e.target.value)}
                >
                  <MenuItem value="">Select Primary Assignee</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.firstName} {user.lastName} ({user.primaryRole})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Secondary Assignee</InputLabel>
                <Select
                  value={formData.secondaryAssignee}
                  label="Secondary Assignee"
                  onChange={(e) => handleChange('secondaryAssignee', e.target.value)}
                >
                  <MenuItem value="">Select Secondary Assignee</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.firstName} {user.lastName} ({user.primaryRole})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Tertiary Assignee</InputLabel>
                <Select
                  value={formData.tertiaryAssignee}
                  label="Tertiary Assignee"
                  onChange={(e) => handleChange('tertiaryAssignee', e.target.value)}
                >
                  <MenuItem value="">Select Tertiary Assignee</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.firstName} {user.lastName} ({user.primaryRole})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.skipConsultation || false}
                    onChange={(e) => handleChange('skipConsultation', e.target.checked)}
                    name="skipConsultation"
                    color="primary"
                  />
                }
                label="I have this property's agency agreement ready"
              />
              {formData.skipConsultation && (
                <Box sx={{ mt: 2 }}>
                    <input
                        accept=".pdf,.doc,.docx"
                        style={{ display: 'none' }}
                        id="agency-agreement-upload"
                        type="file"
                        onChange={(e) => {
                             if (e.target.files && e.target.files.length > 0) {
                                 handleChange('agencyAgreementFile', e.target.files[0]);
                             }
                        }}
                    />
                    <label htmlFor="agency-agreement-upload">
                        <Button variant="outlined" component="span">
                           Upload Agency Agreement (Optional)
                        </Button>
                    </label>
                    {(formData as any).agencyAgreementFile && (
                         <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            {(formData as any).agencyAgreementFile.name}
                         </Typography>
                    )}
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/agencies/${agencySlug}/properties`)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Create Property'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default NewProperty; 